import { useMemo } from "react";
import Section from "./Section";
import Row from "./Row";
import Select from "./Select";
import Input from "./Input";
import "./TestConfigForm.css";

const TEST_TYPES = [
  "technical",
  "technical_multi",
  "iq",
  "english_grammar",
  "english_communication",
  "domain_knowledge",
];

const TYPE_LABELS = {
  technical: "Technical",
  technical_multi: "Technical (Multi answers)",
  iq: "IQ",
  english_grammar: "English Grammar",
  english_communication: "English Communication",
  domain_knowledge: "Domain Knowledge",
};

const DEFAULT_CONFIG_BY_TYPE = {
  technical: { enabled: true, language: "vi", difficulty: "job", question_count: 2 },
  technical_multi: { enabled: true, language: "vi", difficulty: "job", question_count: 2 },
  iq: { enabled: true, language: "vi", question_count: 2 },
  english_grammar: { enabled: true, language: "en", english_level: "B2", question_count: 2 },
  english_communication: { enabled: true, language: "en", english_level: "B2", question_count: 2 },
  domain_knowledge: { enabled: true, language: "vi", question_count: 2 },
};

function TestConfigForm({
  value,
  durationMinutes,
  onDurationChange,
  onChange,
  onSave,
  onCancel,
}) {
  const activeTypes = useMemo(() => Object.keys(value || {}), [value]);

  const addModule = () => {
    const nextType = TEST_TYPES.find((t) => !activeTypes.includes(t));
    if (!nextType) return;

    onChange({
      ...value,
      [nextType]: DEFAULT_CONFIG_BY_TYPE[nextType],
    });
  };

  const removeModule = (type) => {
    const next = { ...value };
    delete next[type];
    onChange(next);
  };

  const update = (type, field, v) => {
    onChange({
      ...value,
      [type]: {
        ...value[type],
        [field]: v,
      },
    });
  };

  const changeType = (oldType, newType) => {
    if (oldType === newType) return;
    if (value[newType]) return;

    const next = { ...value };
    delete next[oldType];
    next[newType] = {
      ...DEFAULT_CONFIG_BY_TYPE[newType],
      ...value[oldType],
    };
    onChange(next);
  };

  return (
    <div className="test-config-form scrollable">
      {/* TEST DURATION */}
      <div className="test-duration-bar">
        <span className="duration-label">⏱ Test duration</span>
        <input
          type="number"
          min={1}
          value={durationMinutes}
          onChange={(e) => onDurationChange(Number(e.target.value))}
        />
        <span className="duration-unit">minutes</span>
      </div>

      {/* MODULES */}
      {activeTypes.map((type) => (
        <div key={type} className="test-module-card">
          <div className="test-module-header">
            <Select
              label="Test type"
              value={type}
              options={TEST_TYPES.map((t) => ({
                value: t,
                label: TYPE_LABELS[t] || t,
              }))}
              onChange={(v) => changeType(type, v)}
            />
            <button
              className="remove-btn"
              onClick={() => removeModule(type)}
            >
              ✕
            </button>
          </div>

          <Section title={TYPE_LABELS[type]}>
            {/* TECHNICAL / TECHNICAL_MULTI */}
            {(type === "technical" || type === "technical_multi") && (
              <Row>
                <Select
                  label="Language"
                  value={value[type].language}
                  options={["vi", "en"]}
                  onChange={(v) => update(type, "language", v)}
                />
                <Input
                  label="Question count"
                  type="number"
                  value={value[type].question_count}
                  onChange={(v) =>
                    update(type, "question_count", Number(v))
                  }
                />
              </Row>
            )}

            {/* IQ */}
            {type === "iq" && (
              <Row>
                <Select
                  label="Language"
                  value={value[type].language}
                  options={["vi", "en"]}
                  onChange={(v) => update(type, "language", v)}
                />
                <Input
                  label="Question count"
                  type="number"
                  value={value[type].question_count}
                  onChange={(v) => update(type, "question_count", Number(v))}
                />
              </Row>
            )}

            {/* ENGLISH */}
            {(type === "english_grammar" ||
              type === "english_communication") && (
              <Row>
                <Select
                  label="Level"
                  value={value[type].english_level}
                  options={["B1", "B2", "C1"]}
                  onChange={(v) =>
                    update(type, "english_level", v)
                  }
                />
                <Input
                  label="Question count"
                  type="number"
                  value={value[type].question_count}
                  onChange={(v) =>
                    update(type, "question_count", Number(v))
                  }
                />
              </Row>
            )}

            {/* DOMAIN KNOWLEDGE */}
            {type === "domain_knowledge" && (
              <Row>
                <Select
                  label="Language"
                  value={value[type].language}
                  options={["vi", "en"]}
                  onChange={(v) => update(type, "language", v)}
                />
                <Input
                  label="Question count"
                  type="number"
                  value={value[type].question_count}
                  onChange={(v) => update(type, "question_count", Number(v))}
                />
              </Row>
            )}
          </Section>
        </div>
      ))}

      {/* ADD MODULE */}
      <div className="add-module-bar">
        <button
          onClick={addModule}
          disabled={activeTypes.length >= TEST_TYPES.length}
        >
          ➕ Add test module
        </button>
      </div>

      <div className="modal-buttons">
        <button onClick={onSave}>Save</button>
        <button className="cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default TestConfigForm;
